import { Link, useParams } from "react-router-dom";
import { getNewsById, getCategoryName, relatedNews, formatViews } from "@/lib/content";
import NewsCard from "@/components/base/NewsCard";
import ShareBar from "@/components/base/ShareBar";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function NewsDetailPage() {
  const { id = "" } = useParams();
  const item = getNewsById(id);

  usePageTitle(
    item ? `${item.title} - ${getCategoryName(item.category)} - 91.con` : "资讯详情 - 91.con",
    item ? item.summary : undefined,
  );

  if (!item) {
    return (
      <div className="mx-auto flex w-full flex-col items-center gap-4 px-4 py-24">
        <i className="ri-error-warning-line text-4xl text-primary-400"></i>
        <p className="text-foreground-700">未找到该资讯，可能已被移除。</p>
        <Link
          to="/"
          className="rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = relatedNews(item, 3);
  const categoryName = getCategoryName(item.category);

  return (
    <article className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${item.category}`} className="transition-colors hover:text-primary-400">
          {categoryName}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="line-clamp-1 text-foreground-800">{item.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          <header className="mb-6">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-sm bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                {categoryName}
              </span>
              {item.hot && (
                <span className="flex items-center gap-1 rounded-sm bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-300">
                  <i className="ri-fire-fill"></i>热点
                </span>
              )}
            </div>
            <h1 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {item.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line text-primary-400"></i>
                {item.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-primary-400"></i>
                {item.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-primary-400"></i>
                {formatViews(item.views)} 阅读
              </span>
            </div>
          </header>

          <div className="mb-6 h-64 w-full overflow-hidden rounded-lg md:h-96">
            <img
              src={item.cover}
              alt={`${item.title} - 娱乐资讯配图`}
              title={`${item.title} - 91.con娱乐资讯`}
              className="h-full w-full object-top"
            />
          </div>

          <div className="prose-content flex flex-col gap-4 text-base leading-relaxed text-foreground-800">
            {item.content.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {item.tags.map((t) => (
              <span
                key={t}
                className="rounded-full border border-background-200 px-3 py-1 text-xs text-foreground-600"
              >
                #{t}
              </span>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-foreground-600">
              <i className="ri-megaphone-line mr-1 text-accent-400"></i>
              内容为前端演示数据，待后端数据接口接入
            </div>
            <ShareBar title={item.title} />
          </div>
        </div>

        <aside className="flex flex-col gap-5">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-links-line text-primary-400"></i>相关资讯
            </h3>
            <div className="flex flex-col gap-4">
              {related.length > 0 ? (
                related.map((r) => (
                  <Link key={r.id} to={`/news/${r.id}`} className="group flex gap-3">
                    <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-md">
                      <img
                        src={r.cover}
                        alt={`${r.title} - 相关资讯`}
                        title={`${r.title} - 91.con娱乐资讯`}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0">
                      <h4 className="line-clamp-2 text-sm text-foreground-800 transition-colors group-hover:text-primary-400">
                        {r.title}
                      </h4>
                      <span className="mt-1 block text-xs text-foreground-500">{r.date}</span>
                    </div>
                  </Link>
                ))
              ) : (
                <p className="text-sm text-foreground-500">暂无相关资讯。</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-bookmark-line text-accent-400"></i>热门推荐
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {relatedNews(item, 2).slice(0, 2).map((r) => (
                <NewsCard key={r.id} item={r} variant="row" />
              ))}
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}